import React, { useState, useRef } from 'react';
import { devLog, devError } from '../../../utils/devLogger';
import GenericListUI, { FetchParams, FetchResult } from '../../../components/CustomList/GenericListUI';
import { ColumnDefinition } from '../../../components/CustomList/GenericDataTable';
import AuctionDetailPopup from './AuctionDetailPopup';
import PreBidDetailPopup from './PreBidDetailPopup';
import { getLiveBiddingMonitor } from '../../../lib/api/admin/adminApi';
import dayjs from 'dayjs';

// 실시간 입찰 모니터링 데이터 타입 정의 (API 응답에 맞춤)
interface LiveBiddingMonitoringData {
  id?: number; // UI에서 사용할 고유 ID
  no: number; // 서버에서 오는 no 그대로 사용
  createAt: string; // 등록일시
  auctioneerName: string; // 경매사
  auctionName: string; // 경매명
  lotIndex: number; // Lot 인덱스 (소켓 연결용)
  lotCode: string; // Lot ID (서버 제공 코드, 예: LOT01)
  lotName: string; // Lot 명
  startPrice: number; // 시작가 KRW
  startPriceUsd: number; // 시작가 USD
  currentPrice: number; // 현재가 KRW (highestPrice)
  currentPriceUsd: number; // 현재가 USD (highestPriceUsd)
  ended: boolean; // 상태 (ended 값으로 판단)
  auctionIndex: number; // 소켓 연결을 위한 auctionIndex 추가
  auctionStartAt: string; // 경매 시작 시간
}

const RealTimeMonitoringPage: React.FC = () => {
  const [refreshTrigger] = useState(0);
  const listRef = useRef<{ refetch: () => void }>(null);
  
  // 팝업 상태
  const [isDetailPopupOpen, setIsDetailPopupOpen] = useState(false);
  const [selectedAuction, setSelectedAuction] = useState<LiveBiddingMonitoringData | null>(null);

  // 사전입찰 팝업 상태
  const [isPreBidPopupOpen, setIsPreBidPopupOpen] = useState(false);
  const [selectedAuctionIndex, setSelectedAuctionIndex] = useState<number>(0);

  // 가격 포맷팅 함수
  const formatPrice = (price: number) => {
    return `₩${price.toLocaleString()}`;
  };

  // 상태 표시를 위한 헬퍼 함수
  const getStatusText = (ended: boolean, auctionStartAt: string) => {
    if (ended) {
      return '종료';
    }
    
    // auctionStartAt이 없으면 기본적으로 '진행예정'
    if (!auctionStartAt) {
      return '진행예정';
    }
    
    const currentTime = dayjs();
    const startTime = dayjs(auctionStartAt);
    
    // 현재 시간이 시작 시간보다 후면 '진행중', 아니면 '진행예정'
    return currentTime.isAfter(startTime) ? '진행중' : '진행예정';
  };

  // 컬럼 정의 (요구사항에 따른 순서로 배치)
  const columns: ColumnDefinition<LiveBiddingMonitoringData>[] = [
    {
      header: 'No',
      accessor: 'no',
      formatter: (value) => value?.toString() || '',
    },
    {
      header: '등록일시',
      accessor: 'createAt',
      formatter: (value) => {
        if (!value) return '-';
        return dayjs(value).format('YYYY.MM.DD HH:mm:ss');
      },
    },
    {
      header: '경매사',
      accessor: 'auctioneerName',
      formatter: (value) => value || '-',
    },
    {
      header: '경매명',
      accessor: 'auctionName',
      formatter: (value) => value || '-',
    },
    {
      header: 'Lot ID',
      accessor: 'lotCode',
      formatter: (value) => value || '-',
    },
    {
      header: 'Lot 명',
      accessor: 'lotName',
      formatter: (value) => value || '-',
    },
    {
      header: '시작가 KRW',
      accessor: 'startPrice',
      formatter: (value) => formatPrice(value as number),
    },
    {
      header: '시작가 USD',
      accessor: 'startPriceUsd',
      formatter: (value) => `$${(value as number).toFixed(2)}`,
    },
    {
      header: '현재가 KRW',
      accessor: 'currentPrice',
      formatter: (value) => formatPrice(value as number),
    },
    {
      header: '현재가 USD',
      accessor: 'currentPriceUsd',
      formatter: (value) => `$${(value as number).toFixed(2)}`,
    },
    {
      header: '상태',
      accessor: 'ended',
      formatter: (value, row) => {
        if (!row) return '알 수 없음';
        return getStatusText(value as boolean, row.auctionStartAt);
      },
      excelFormatter: (value, row) => {
        if (!row) return '알 수 없음';
        return getStatusText(value as boolean, row.auctionStartAt);
      },
    },
  ];

  // 실시간 입찰 모니터링 데이터를 가져오는 API 함수
  const fetchLiveBiddingData = async (params: FetchParams): Promise<FetchResult<LiveBiddingMonitoringData>> => {
    try {
      devLog('fetchLiveBiddingData API 호출:', params);
      
      // API 호출
      const response = await getLiveBiddingMonitor({
        keyword: params.keyword || '',
        fromDate: params.fromDate || '2025-01-01',
        toDate: params.toDate || '2025-12-31',
      });

      devLog('API 응답:', response);

      // 메타데이터에서 총 개수 정보 가져오기 (response.data에 포함된 metadata)
      const fullResponse = response?.data || {};
      const metadata = fullResponse.metadata || {};
      const actualData = fullResponse.data || [];
      
      // actualData가 배열인지 확인
      if (!Array.isArray(actualData)) {
        devError('API 응답 data.data가 배열이 아닙니다:', actualData);
        return {
          data: [],
          totalItems: 0,
          allItems: 0,
        };
      }
      
      // UI용 데이터 변환 (요구사항에 맞춰 수정)
      const transformedData: LiveBiddingMonitoringData[] = actualData.map((item: any, index: number) => ({
        id: item.index || index + 1,
        no: item.no, // 서버에서 오는 no 그대로 사용
        createAt: item.createAt, // 등록일시
        auctioneerName: item.auctioneer?.name || '경매사 없음', // 경매사
        auctionName: item.auction?.name || '경매명 없음', // 경매명
        lotIndex: item.index, // Lot 인덱스 (소켓 연결용)
        lotCode: item.code || `LOT.${item.lotNo || item.index}`, // 서버 제공 코드 우선 사용
        lotName: item.name, // Lot 명
        startPrice: item.startPrice || 0, // 시작가 KRW
        startPriceUsd: item.startPriceUsd || 0, // 시작가 USD
        currentPrice: item.highestPrice || item.startPrice || 0, // 현재가 KRW
        currentPriceUsd: item.highestPriceUsd || item.startPriceUsd || 0, // 현재가 USD
        ended: item.ended || false, // 상태
        auctionIndex: item.auction?.index || 0, // 소켓 연결을 위한 auctionIndex 추가
        auctionStartAt: item.auction?.auctionStartAt || '', // 경매 시작 시간
      }));
      
      const totalItems = metadata.totalCnt || transformedData.length;
      const allItems = metadata.allCnt || transformedData.length;
      
      // API 응답을 FetchResult 형태로 변환
      const result: FetchResult<LiveBiddingMonitoringData> = {
        data: transformedData,
        totalItems: totalItems,
        allItems: allItems,
      };
      
      return result;
      
    } catch (error) {
      devError('실시간 입찰 모니터링 데이터 조회 에러:', error);
      
      // 에러 시 빈 결과 반환
      return {
        data: [],
        totalItems: 0,
        allItems: 0,
      };
    }
  };

  // 행 클릭 핸들러 - 경매 상세 정보 팝업 또는 사전입찰 팝업 열기
  const handleRowClick = (item: LiveBiddingMonitoringData, index: number) => {
    devLog('🎯 [RealTimeMonitoring] 행 클릭:', {
      item,
      auctionIndex: item.auctionIndex,
      lotIndex: item.lotIndex,
      auctionName: item.auctionName
    });
    
    // 상태 확인
    const status = getStatusText(item.ended, item.auctionStartAt);
    
    if (status === '진행예정') {
      // 진행예정 상태일 때는 사전입찰 팝업 표시
      setSelectedAuctionIndex(item.auctionIndex);
      setIsPreBidPopupOpen(true);
    } else {
      // 진행중이나 종료 상태일 때는 기존 팝업 표시
      setSelectedAuction(item);
      setIsDetailPopupOpen(true);
    }
  };

  // 팝업 닫기
  const closeDetailPopup = () => {
    setIsDetailPopupOpen(false);
    setSelectedAuction(null);
  };

  // 사전입찰 팝업 닫기
  const closePreBidPopup = () => {
    setIsPreBidPopupOpen(false);
    setSelectedAuctionIndex(0);
  };

  return (
    <>
      <GenericListUI<LiveBiddingMonitoringData>
        ref={listRef}
        title="실시간 모니터링"
        columns={columns}
        fetchData={fetchLiveBiddingData}
        excelFileName="LiveBiddingMonitoring"
        keyExtractor={(item, index) => {
          // 고유한 키 생성을 위해 여러 속성을 조합
          const uniqueKey = `${item.auctionIndex || 0}-${item.lotIndex || 0}-${item.no || index}`;
          return uniqueKey;
        }}
        enableSearch={true}
        searchPlaceholder="경매사, 경매명, LOT명으로 검색"
        enableDateFilter={true}
 dateRangeOptions={['금월', '3개월', '6개월', '지정']}
        itemsPerPageOptions={[10, 20, 50, 100]}
        themeMode="light"
        onRowClick={handleRowClick}
        refreshTrigger={refreshTrigger}
      />

      {/* 팝업이 필요한 경우에만 렌더링 */}
      {isDetailPopupOpen && selectedAuction && (
        <AuctionDetailPopup
          isOpen={isDetailPopupOpen}
          auctionData={null} // API 타입이 맞지 않으므로 일단 null로 설정
          auctionIndex={selectedAuction.auctionIndex} // 소켓 연결을 위한 auctionIndex 전달
          selectedLotIndex={selectedAuction.lotIndex} // 선택된 lot index 전달
          onClose={closeDetailPopup}
        />
      )}

      {/* 사전입찰 팝업 */}
      {isPreBidPopupOpen && selectedAuctionIndex > 0 && (
        <PreBidDetailPopup
          isOpen={isPreBidPopupOpen}
          auctionIndex={selectedAuctionIndex}
          onClose={closePreBidPopup}
        />
      )}
    </>
  );
};

export default RealTimeMonitoringPage;
